import React from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ArrowLeft, AlertTriangle, Info, Clock } from 'lucide-react';
import { OOCForecastAdjustment, OOCForecastCell, OOCTabType, formatOOCNumber } from '../oocForecast.type';
import { useOOCSCReviewLogic, TAB_LABELS, getCellClass } from './oocSCReview.logic';
import styles from './oocSCReview.module.scss';

const forecastCellTemplate = (rowData: OOCForecastAdjustment, periodIndex: number) => {
  const cell: OOCForecastCell | undefined = rowData.forecasts[periodIndex];
  if (!cell) return '-';
  const displayValue = cell.value !== null && cell.value !== undefined ? formatOOCNumber(cell.value) : '-';
  return (
    <div className={getCellClass(cell.colorType, styles)}>
      {cell.previousValue !== null && cell.previousValue !== undefined && cell.colorType === 'modified' && (
        <span className={styles.previousValue}>was {formatOOCNumber(cell.previousValue)}</span>
      )}
      {displayValue}
    </div>
  );
};

interface TabContentProps {
  adjustments: OOCForecastAdjustment[];
  tabType: OOCTabType;
}

const TabContent: React.FC<TabContentProps> = ({ adjustments, tabType }) => {
  if (adjustments.length === 0) return null;
  const monthColumns = adjustments[0]?.forecasts ?? [];
  const isProvMas = tabType === 'ProvMAS';

  return (
    <div>
      <div className={styles.infoCallout}>
        <Info size={16} className={styles.calloutIcon} />
        <span>Forecast adjustments below are read-only for SC. Review and update EBQ/ROP in Syspro accordingly.</span>
      </div>

      {isProvMas && (
        <div className={styles.provWarningCallout}>
          <Clock size={16} className={styles.calloutIcon} />
          <span>
            Provisional MAS forecasts have a 7-day sharing tracker. Ensure supplier notifications are sent within the SLA window.
          </span>
        </div>
      )}

      <div className={styles.tableWrapper}>
        <DataTable value={adjustments} scrollable scrollHeight="400px">
          <Column field="ndc10" header="NDC10" frozen style={{ minWidth: '100px' }} />
          <Column field="ndc11" header="NDC11" frozen style={{ minWidth: '110px' }} />
          <Column field="supplier" header="Supplier" style={{ minWidth: '130px' }} />
          <Column field="concept" header="Concept" style={{ minWidth: '120px' }} />
          <Column field="packOut" header="Pack Out" style={{ minWidth: '80px' }} />
          {monthColumns.map((_, idx) => (
            <Column
              key={idx}
              header={adjustments[0].forecasts[idx]?.period ?? `M${idx + 1}`}
              body={(rowData: OOCForecastAdjustment) => forecastCellTemplate(rowData, idx)}
              style={{ minWidth: '75px', textAlign: 'center' }}
            />
          ))}
          {isProvMas && (
            <Column
              field="probability"
              header="Probability"
              style={{ minWidth: '95px' }}
              body={(rowData: OOCForecastAdjustment) => rowData.probability ?? '-'}
            />
          )}
          <Column
            field="notes"
            header="Notes"
            style={{ minWidth: '150px' }}
            body={(rowData: OOCForecastAdjustment) => rowData.notes || '-'}
          />
        </DataTable>
      </div>
    </div>
  );
};

const OOCSCReview: React.FC = () => {
  const {
    currentSubmission,
    navigateToDashboard,
    activeTab: _activeTab,
    setActiveTab,
    tabGroups,
    availableTabs,
    currentActiveTab,
    bulkChangeCount,
    feedbackDeadline,
  } = useOOCSCReviewLogic();

  if (!currentSubmission) {
    return <div className={styles.scReviewContainer}>No submission selected.</div>;
  }

  const { id } = currentSubmission;

  return (
    <div className={styles.scReviewContainer}>
      <div className={styles.sectionHeader}>
        <h2>{id} - Supply Chain Review</h2>
      </div>

      {bulkChangeCount > 0 && (
        <div className={styles.warningCallout}>
          <AlertTriangle size={16} className={styles.calloutIcon} />
          <span>
            <strong>Supplier Notification Required:</strong> {bulkChangeCount} NDCs have bulk-level changes exceeding +/-20%. 48-hour feedback deadline: {feedbackDeadline}.
          </span>
        </div>
      )}

      {availableTabs.length > 0 && (
        <>
          <div className={styles.tabsRow}>
            {availableTabs.map((tab) => {
              const count = tabGroups[tab]?.length ?? 0;
              const isActive = currentActiveTab === tab;
              return (
                <button
                  key={tab}
                  className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {TAB_LABELS[tab]}
                  <span className={`${styles.tabCount} ${isActive ? styles.tabCountActive : ''}`}>{count}</span>
                </button>
              );
            })}
          </div>
          <TabContent adjustments={tabGroups[currentActiveTab] ?? []} tabType={currentActiveTab} />
        </>
      )}

      <div className={styles.actionBar}>
        <Button
          label="Back to Dashboard"
          icon={<ArrowLeft size={16} />}
          className={`p-button-text ${styles.backBtn}`}
          onClick={navigateToDashboard}
        />
      </div>
    </div>
  );
};

export default observer(OOCSCReview);
